import { create } from 'zustand';
import { FormField, FieldValue, FormStructure } from '../types/forms';
import {
  validateFederalTaxId,
  validatePhoneNumber,
  validateZipCode,
  validateEmail,
  validateOwnershipPercentage,
  validateSSN,
  validateDateOfBirth,
} from '../utils/validation.utils';
import { logger } from '@/features/help/utils/logger';

interface ValidationStore {
  invalidFields: string[];
  invalidFieldsPageMap: Record<string, string>;
  fieldErrors: Record<string, string>;
  validateField: (
    field: FormField,
    valueToValidate?: FieldValue,
    pageId?: string
  ) => Promise<{ isValid: boolean; errorMessage: string }>;
  validatePage: (
    pageId: string,
    structure: FormStructure,
    viewableFields: string[],
    editableFields: string[],
    files: Record<string, File>
  ) => Promise<boolean>;
  validateAllPages: (
    structure: FormStructure,
    viewableFields: string[],
    editableFields: string[],
    files: Record<string, File>
  ) => Promise<{
    isValid: boolean;
    firstInvalidPageIndex: number | null;
    invalidFieldsByType: Record<string, string[]>;
  }>;
  addInvalidField: (fieldId: string, pageId: string, errorMessage: string) => void;
  removeInvalidField: (fieldId: string) => void;
  clearInvalidFields: () => void;
}

export const useValidationStore = create<ValidationStore>((set, get) => ({
  invalidFields: [],
  invalidFieldsPageMap: {},
  fieldErrors: {},

  addInvalidField: (fieldId, pageId, errorMessage) => {
    set((state) => {
      if (!state.invalidFields.includes(fieldId)) {
        return {
          invalidFields: [...state.invalidFields, fieldId],
          invalidFieldsPageMap: { ...state.invalidFieldsPageMap, [fieldId]: pageId },
          fieldErrors: { ...state.fieldErrors, [fieldId]: errorMessage },
        };
      }
      // Update error message even if field is already in invalid list
      return {
        ...state,
        fieldErrors: { ...state.fieldErrors, [fieldId]: errorMessage },
      };
    });
  },

  removeInvalidField: (fieldId) => {
    set((state) => {
      if (state.invalidFields.includes(fieldId)) {
        const newInvalidFields = state.invalidFields.filter((id) => id !== fieldId);
        const newInvalidFieldsPageMap = { ...state.invalidFieldsPageMap };
        const newFieldErrors = { ...state.fieldErrors };
        delete newInvalidFieldsPageMap[fieldId];
        delete newFieldErrors[fieldId];
        return {
          invalidFields: newInvalidFields,
          invalidFieldsPageMap: newInvalidFieldsPageMap,
          fieldErrors: newFieldErrors,
        };
      }
      return state;
    });
  },

  clearInvalidFields: () => {
    set({ invalidFields: [], invalidFieldsPageMap: {}, fieldErrors: {} });
  },

  validateField: async (
    field: FormField,
    valueToValidate?: FieldValue,
    pageId?: string
  ): Promise<{ isValid: boolean; errorMessage: string }> => {
    const { addInvalidField, removeInvalidField } = get();
    const value = valueToValidate !== undefined ? valueToValidate : field.value;
    let isValid = true;
    let errorMessage = '';

    // Purpose: Validate required picklists more strictly (must be a valid option)
    if (field.type === 'picklist' && typeof value === 'string') {
      const trimmed = value.trim();
      const allowedValues =
        Array.isArray(field.crmPicklistValues) && field.crmPicklistValues.length > 0
          ? field.crmPicklistValues.map((o) => String(o.value))
          : Array.isArray(field.currentPicklistOptions)
            ? field.currentPicklistOptions.map((o) => String(o.label))
            : [];

      // If we have a defined option set, ensure the value is one of them.
      if (trimmed && allowedValues.length > 0 && !allowedValues.includes(trimmed)) {
        isValid = false;
        errorMessage = 'Please select a valid option.';
      }
    }

    if (
      field.required &&
      (value === '' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0))
    ) {
      isValid = false;
      errorMessage = 'This field is required.';
    } else if (value) {
      const fieldId = field.id || '';
      const isLegacyFederalTaxIdField = fieldId === 'federalTaxId';
      const isLegacySsnField = fieldId.includes('SSN') || fieldId.includes('SocialSecurity');

      if (isLegacyFederalTaxIdField) {
        isValid = validateFederalTaxId(value as string);
        if (!isValid) errorMessage = 'Invalid Federal Tax ID.';
      } else if (isLegacySsnField) {
        isValid = validateSSN(value as string);
        if (!isValid) errorMessage = 'Invalid SSN format.';
      } else {
        switch (field.type) {
          case 'email':
            isValid = validateEmail(value as string);
            if (!isValid) errorMessage = 'Invalid email format.';
            break;
          case 'phone':
            isValid = validatePhoneNumber(value as string);
            if (!isValid) errorMessage = 'Invalid phone number format.';
            break;
          case 'date':
          case 'datetime':
            if (field.id.toLowerCase().includes('birth')) {
              isValid = validateDateOfBirth(value as string);
              if (!isValid) errorMessage = 'Must be at least 18 years old.';
            } else {
              // For other dates, just validate the date format
              const date = new Date(value as string);
              isValid =
                !isNaN(date.getTime()) && date.getFullYear() >= 1700 && date.getFullYear() <= 4000;
              if (!isValid) errorMessage = 'Date must be between 01/01/1700 and 12/31/4000.';
            }
            break;
          case 'tax-id':
            isValid = validateFederalTaxId(value as string);
            if (!isValid) errorMessage = 'Invalid Federal Tax ID.';
            break;
          case 'zip-code':
            isValid = validateZipCode(value as string);
            if (!isValid) errorMessage = 'Invalid zip code.';
            break;

          case 'ownership-percentage':
            isValid = validateOwnershipPercentage(value as string);
            if (!isValid)
              errorMessage = 'Invalid ownership percentage (must be between 0 and 100).';
            break;
          case 'ssn':
            isValid = validateSSN(value as string);
            if (!isValid) errorMessage = 'Invalid SSN format.';
            break;
        }
      }
    }

    if (isValid) {
      removeInvalidField(field.id);
    } else {
      addInvalidField(field.id, pageId || '', errorMessage);
    }

    return { isValid, errorMessage };
  },

  validatePage: async (
    pageId: string,
    structure: FormStructure,
    viewableFields: string[],
    editableFields: string[],
    files: Record<string, File>
  ) => {
    const {
      invalidFields: currentInvalidFields,
      invalidFieldsPageMap: currentInvalidFieldsPageMap,
    } = get();

    const page = structure.pages.find((p) => p.id === pageId);
    if (!page) {
      logger.warn('Validation Store', 'validatePage: Page not found in structure', { pageId });
      return false;
    }

    // Clear previous invalid field markers for the current page only
    const updatedInvalidFields = currentInvalidFields.filter(
      (fieldId) => currentInvalidFieldsPageMap[fieldId] !== pageId
    );
    const updatedInvalidFieldsPageMap = { ...currentInvalidFieldsPageMap };
    currentInvalidFields.forEach((fieldId) => {
      if (currentInvalidFieldsPageMap[fieldId] === pageId) {
        delete updatedInvalidFieldsPageMap[fieldId];
      }
    });
    set({ invalidFields: updatedInvalidFields, invalidFieldsPageMap: updatedInvalidFieldsPageMap });

    let isPageOverallValid = true;

    for (const section of page.sections) {
      for (const field of section.fields) {
        if (!viewableFields.includes(field.id)) {
          get().removeInvalidField(field.id);
          continue;
        }

        if (!editableFields.includes(field.id)) {
          get().removeInvalidField(field.id);
          continue;
        }

        // Enhanced validation for file fields
        const fieldValue = field.value;
        if (field.type === 'file') {
          // Check if files exist in the files map
          const fileActuallyExists =
            !!files[field.id] || Object.keys(files).some((key) => key.startsWith(`${field.id}_`));

          if (!fileActuallyExists && field.required) {
            const result = await get().validateField(field, field.value, pageId);
            if (!result.isValid) {
              isPageOverallValid = false;
            }
            continue;
          }
        }

        // Enhanced validation for signature fields
        if (field.type === 'signature' && field.required) {
          if (
            !fieldValue ||
            typeof fieldValue !== 'string' ||
            !fieldValue.startsWith('data:image/')
          ) {
            const result = await get().validateField(field, field.value, pageId);
            if (!result.isValid) {
              isPageOverallValid = false;
            }
            continue;
          }
        }

        const result = await get().validateField(field, fieldValue, pageId);
        if (!result.isValid) {
          isPageOverallValid = false;
        }
      }
    }

    if (!isPageOverallValid) {
      logger.info('Validation Store', 'Page validation failed', { pageId });
    }

    return isPageOverallValid;
  },

  validateAllPages: async (
    structure: FormStructure,
    viewableFields: string[],
    editableFields: string[],
    files: Record<string, File>
  ) => {
    if (!structure) {
      logger.warn('Validation Store', 'validateAllPages: No structure found');
      return { isValid: false, firstInvalidPageIndex: null, invalidFieldsByType: {} };
    }

    set({ invalidFields: [], invalidFieldsPageMap: {}, fieldErrors: {} });

    let isFormOverallValid = true;
    let firstInvalidPageIndex: number | null = null;

    for (let i = 0; i < structure.pages.length; i++) {
      const page = structure.pages[i];
      const isPageValid = await get().validatePage(
        page.id,
        structure,
        viewableFields,
        editableFields,
        files
      );

      if (!isPageValid && firstInvalidPageIndex === null) {
        firstInvalidPageIndex = i;
        logger.warn('Validation Store', 'validateAllPages: Found first invalid page', {
          pageIndex: i,
          pageId: page.id,
          pageTitle: page.title,
        });
      }
      if (!isPageValid) {
        isFormOverallValid = false;
      }
    }

    const { invalidFields: currentlyInvalidFieldIds } = get();
    const invalidFieldsByType: Record<string, string[]> = {
      required: [],
      email: [],
      phone: [],
      signature: [],
      federalTaxId: [],
      zipCode: [],
      ownershipPercentage: [],
      ssn: [],
      dateOfBirth: [],
      file: [],
      terms: [],
    };

    if (structure && currentlyInvalidFieldIds.length > 0) {
      currentlyInvalidFieldIds.forEach((fieldId) => {
        let fieldDefinition: FormField | null = null;
        for (const p of structure.pages) {
          for (const s of p.sections) {
            const f = s.fields.find((sf) => sf.id === fieldId);
            if (f) {
              fieldDefinition = f;
              break;
            }
          }
          if (fieldDefinition) break;
        }

        if (fieldDefinition) {
          const value = fieldDefinition.value;
          let isRequiredIssue = false;

          // Check for required field issues
          if (fieldDefinition.required) {
            if (
              fieldDefinition.type === 'picklist' &&
              (value === null || value === undefined || String(value).trim() === '')
            )
              isRequiredIssue = true;
            else if (
              fieldDefinition.type === 'signature' &&
              (!value || typeof value !== 'string' || !value.startsWith('data:image/'))
            )
              isRequiredIssue = true;
            else if (fieldDefinition.type === 'file') {
              let fileActuallyExists = false;
              if (Array.isArray(value)) {
                fileActuallyExists = value.length > 0;
              } else if (
                value &&
                typeof value === 'object' &&
                'name' in value &&
                'contentType' in value
              ) {
                fileActuallyExists = true;
              }
              if (!fileActuallyExists) {
                fileActuallyExists =
                  !!files[fieldId] ||
                  Object.keys(files).some((key) => key.startsWith(`${fieldId}_`));
              }
              if (!fileActuallyExists) isRequiredIssue = true;
            } else if (fieldDefinition.type === 'terms' && value !== true) isRequiredIssue = true;
            else if (
              value === null ||
              value === undefined ||
              (typeof value === 'string' && String(value).trim() === '') ||
              (Array.isArray(value) && value.length === 0)
            ) {
              isRequiredIssue = true;
            }
          }

          if (isRequiredIssue) {
            invalidFieldsByType.required.push(fieldId);
          } else {
            // Check format issues for non-empty values
            const sValue = value && typeof value === 'string' ? String(value).trim() : '';
            if (sValue) {
              if (fieldDefinition.id === 'federalTaxId' && !validateFederalTaxId(sValue))
                invalidFieldsByType.federalTaxId.push(fieldId);
              else if (fieldDefinition.id === 'businessZip' && !validateZipCode(sValue))
                invalidFieldsByType.zipCode.push(fieldId);
              else if (
                (fieldDefinition.type === 'email' ||
                  fieldDefinition.id.toLowerCase().includes('email')) &&
                !validateEmail(sValue)
              )
                invalidFieldsByType.email.push(fieldId);
              else if (
                (fieldDefinition.type === 'phone' ||
                  fieldDefinition.id.toLowerCase().includes('phone') ||
                  fieldDefinition.id === 'fax') &&
                !validatePhoneNumber(sValue)
              )
                invalidFieldsByType.phone.push(fieldId);
              else if (
                fieldDefinition.id.includes('OwnershipPercentage') &&
                !validateOwnershipPercentage(sValue)
              )
                invalidFieldsByType.ownershipPercentage.push(fieldId);
              else if (
                (fieldDefinition.id.includes('SSN') ||
                  fieldDefinition.id.includes('SocialSecurity')) &&
                !validateSSN(sValue)
              )
                invalidFieldsByType.ssn.push(fieldId);
              else if (
                (fieldDefinition.id.includes('DateOfBirth') ||
                  fieldDefinition.id.includes('DOB')) &&
                !validateDateOfBirth(sValue)
              )
                invalidFieldsByType.dateOfBirth.push(fieldId);
            }
          }

          // Specific type categories even if not format related
          if (fieldDefinition.type === 'signature' && isRequiredIssue)
            invalidFieldsByType.signature.push(fieldId);
          if (fieldDefinition.type === 'file' && isRequiredIssue)
            invalidFieldsByType.file.push(fieldId);
          if (fieldDefinition.type === 'terms' && isRequiredIssue)
            invalidFieldsByType.terms.push(fieldId);
        }
      });
    }

    logger.info('Validation Store', 'validateAllPages: Form validation complete', {
      isValid: isFormOverallValid,
      firstInvalidPageIndex,
      invalidFieldCount: currentlyInvalidFieldIds.length,
      invalidFieldsByTypeSummary: Object.fromEntries(
        Object.entries(invalidFieldsByType)
          .map(([k, v]) => [k, v.length > 0 ? v : undefined])
          .filter(([_, v]) => v !== undefined)
      ),
    });

    return { isValid: isFormOverallValid, firstInvalidPageIndex, invalidFieldsByType };
  },
}));
